<template>
  <DialogContent class="flex flex-col items-center text-center">
    <LockedFile
      class="mt-2.5 tablet-h:mt-5 w-[70px] -ml-1.5 tablet-h:-ml-3 tablet-h:w-[85px] mb-[26px] tablet-h:mb-[38px]"
    />

    <div
      class="font-primary font-semibold text-[17px] leading-[24px] mb-[19px]"
    >
      Set privacy default for your copyrights
    </div>

    <div class="max-w-[430px] mb-5">
      You are adding your first copyright(s) as
      {{ userStore.user?.defaultFilePrivacy.toLowerCase() }}. Would you like
      this file and future copyrighted files to be private by default?
    </div>

    <div
      class="w-full px-[18px] tablet-h:px-[14px] py-3 tablet-h:py-[13px] rounded-[10px] bg-gray-200/30 text-left mb-1.5 tablet-h:flex"
    >
      <div class="flex justify-between gap-x-2.5 tablet-h:mr-[15px]">
        <div class="tablet-h:hidden">
          <div class="flex items-center mb-2">
            <Icon
              name="Lock"
              :size="20"
              class="stroke-gray-800 mr-1.5"
              :class="classes"
            />
            <div
              class="font-secondary text-[14px] leading-[18px] text-gray-800 font-medium"
              :class="classes"
            >
              Privacy
            </div>
            <Pill v-if="isBasic" class="tablet-h:hidden ml-2.5" success>
              Pro only
            </Pill>
          </div>

          <div
            class="tablet-h:hidden text-[13px] leading-[18px] text-gray-800/75"
            :class="classes"
          >
            <span class="font-medium">Make private</span>. Only you and those
            you invite can see this file and its description
          </div>
        </div>

        <Toggle v-model="isPrivate" :disabled="isBasic" />
      </div>

      <div
        class="w-full hidden tablet-h:block text-[13px] leading-[18px] text-gray-800/75 mt-2 tablet-h:mt-0"
        :class="classes"
      >
        <span class="font-medium">Private by default</span>. Only you and those
        you invite can see the copyrighted file and its description.
      </div>

      <Pill v-if="isBasic" class="hidden tablet-h:block h-fit" success
        >Pro only</Pill
      >
    </div>

    <div class="text-[13px] leading-[22px] text-gray-500">
      This can be changed later in your account settings.
    </div>
  </DialogContent>

  <DialogFooter class="flex flex-col tablet-h:flex-row gap-2.5">
    <template v-if="isBasic">
      <UiButton class="w-full! tablet-h:w-auto!" @click="handleClick">
        Keep public
      </UiButton>

      <RouterLink to="/upgrade" class="w-full tablet-h:w-auto">
        <UiButton class="w-full" primary @click="close()">
          Upgrade to Pro
        </UiButton>
      </RouterLink>
    </template>

    <UiButton
      v-else
      class="w-full! tablet-h:w-auto!"
      primary
      :loading="loading"
      @click="handleClick"
    >
      Continue
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, Pill, Toggle, UiButton } from '@dacr/ui'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import LockedFile from '@/assets/svg/other/LockedFile.svg'
import { useUserStore } from '@/shared/stores/user'
import {
  FilePrivacy,
  MeDocument,
  UserPermission,
  useUpdateAccountDefaultFilePrivacyMutation,
  type MeQuery,
} from '@/shared/graphql/legacy/generated'
import { computed, ref } from 'vue'
import { apolloClient } from '@/shared/graphql/client'

const { close, triggerEvent } = useDialog()

const { mutate: updateAccountDefaultFilePrivacy, loading } =
  useUpdateAccountDefaultFilePrivacyMutation()

const userStore = useUserStore()

const isPrivate = ref(
  userStore.user?.defaultFilePrivacy === FilePrivacy.Private
)

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const classes = computed(() => ({
  'opacity-50': isBasic.value,
}))

const handleClick = async () => {
  const hasChangedValue =
    userStore.user?.defaultFilePrivacy !==
    (isPrivate.value ? FilePrivacy.Private : FilePrivacy.Public)

  if (!isBasic.value && hasChangedValue) {
    await updateAccountDefaultFilePrivacy({
      isPrivate: isPrivate.value,
    })
  }

  apolloClient.cache.updateQuery<MeQuery>(
    {
      query: MeDocument,
    },
    (response) => {
      if (!response) return

      return {
        ...response,
        me: {
          ...response.me,
          hasUploadedFile: true,
        },
      }
    }
  )

  triggerEvent('success')
}
</script>
